import { FC, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { SectionScoreCard } from "../SectionScoreCard";

import { EmptyAssessmentDashboard } from "./EmptyAssessmentDashboard";
import { StudentPerformanceDashboard } from "./StudentPerformanceDashboard";

import { StudentUserAvatar } from "components/materials/page/assessment/AssessmentStudentQueue";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { selectUsersWhoCanParticipateInSection } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { PROFILE_PROPERTIES, getUserFullName } from "utils/user-utils";

export const StudentPerformancePanel: FC = () => {
  const studentUsers = useListSelector(selectUsersWhoCanParticipateInSection, PROFILE_PROPERTIES);

  const options = useMemo(
    () =>
      studentUsers
        .map((user) => ({ title: getUserFullName(user), id: user.id }))
        .toSorted(ORDER_BY_TITLE),
    [studentUsers],
  );

  const [selectedUserId, setSelectedUserId] = useState<string>("");

  const firstOptionId = options[0]?.id;
  useEffect(() => {
    if (!selectedUserId && firstOptionId) {
      setSelectedUserId(firstOptionId);
    }
  }, [firstOptionId, selectedUserId]);

  if (options.length === 0 || !selectedUserId) return <EmptyAssessmentDashboard />;

  return (
    <div className="flex w-full gap-6">
      <div
        className="h-full w-[240px] overflow-y-auto rounded-2xl border border-black-tint-93 bg-white px-3 py-4"
        role="list"
      >
        {options.map((option) => {
          const isSelected = selectedUserId === option.id;
          return (
            <SquareButton
              aria-label={option.title}
              aria-selected={isSelected || undefined}
              className="my-1 w-full justify-start"
              eventName="Assessment Dashboard Student Performance View - Select Student"
              eventProps={{
                userId: option.id,
              }}
              isSelected={isSelected}
              key={option.id}
              kind={isSelected ? "accent" : "ghost"}
              role="option"
              size="s"
              tabIndex={isSelected ? 0 : -1}
              title={option.title}
              onClick={() => setSelectedUserId(option.id)}
            >
              <StudentUserAvatar
                className="bg-blue-tint-93 text-blue"
                isSelected={isSelected}
                userId={option.id}
              />
              <div
                className={twMerge(
                  "min-w-0 truncate text-sm font-medium",
                  isSelected ? "text-white" : "text-black-tint-20",
                )}
              >
                {option.title}
              </div>
            </SquareButton>
          );
        })}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-6">
        <SectionScoreCard userId={selectedUserId} />
        <StudentPerformanceDashboard
          key={selectedUserId}
          studentName={options.find((option) => option.id === selectedUserId)?.title}
          userId={selectedUserId}
        />
      </div>
    </div>
  );
};
